import type { CafeteriaServiceSessionResponse } from '@/api/cafeteria'
import { groupStudentRows } from '@/lib/cafeteria/format'
import { type RosterFilter, rosterFilterLabel } from '@/lib/cafeteria/print-plan'

/** The same enrollment facts and axis resolver as the printed roster. */
export function rosterSelectionOptions(
  sessions: readonly CafeteriaServiceSessionResponse[],
  axis: RosterFilter['axis'],
): string[] {
  const labels = new Set<string>()
  for (const session of sessions) {
    const students = groupStudentRows(session.items.filter((item) => item.source === 'PARENT_ORDER'))
    for (const student of students) {
      labels.add(rosterFilterLabel(student, axis, session.location.groupingPolicy?.serveGroupBy))
    }
  }
  return [...labels].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
}

export interface RosterSelection {
  /** null selects every group, including groups added when the date range grows. */
  labels: ReadonlySet<string> | null
  grouped: boolean
}

export const ALL_ROSTER_GROUPS: RosterSelection = { labels: null, grouped: true }

export function selectedRosterLabels(options: readonly string[], selection: RosterSelection): Set<string> {
  return new Set(options.filter((label) => selection.labels === null || selection.labels.has(label)))
}

export function changeRosterSelection(
  options: readonly string[],
  previous: RosterSelection,
  labels: ReadonlySet<string>,
): RosterSelection {
  const selected = new Set(options.filter((label) => labels.has(label)))
  return {
    labels: selected.size === options.length ? null : selected,
    // Returning from one group to several starts with grouping on again.
    grouped: selected.size <= 1 || selectedRosterLabels(options, previous).size <= 1 || previous.grouped,
  }
}
